/* RecycleBin page — view and restore soft-deleted items.
 * Auto-purge items > 30 days, manual restore/purge for individual items.
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Trash2,
  RotateCcw,
  Building2,
  Users,
  Home,
  Loader2,
  RefreshCw,
  Clock,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

const TYPE_META = {
  brokerage: { icon: Building2, color: "text-blue-600", bg: "bg-blue-50", label: "Brokerage" },
  realtor: { icon: Users, color: "text-emerald-600", bg: "bg-emerald-50", label: "Realtor" },
  open_house: { icon: Home, color: "text-amber-600", bg: "bg-amber-50", label: "Open House" },
};

export default function RecycleBin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [autoPurgeBusy, setAutoPurgeBusy] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recycle-bin");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load recycle bin", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (item) => {
    setRestoring(item.id);
    try {
      await api.post(`/recycle-bin/${item.type}/${item.id}/restore`);
      toast.success(`Restored "${item.name}"`);
      setItems((prev) => prev.filter((i) => !(i.id === item.id && i.type === item.type)));
    } catch (e) {
      toast.error("Restore failed", { description: formatApiError(e) });
    } finally {
      setRestoring(null);
    }
  };

  const purge = async (item) => {
    setPurging(item.id);
    try {
      await api.delete(`/recycle-bin/${item.type}/${item.id}/purge`);
      toast.success("Permanently deleted");
      setItems((prev) => prev.filter((i) => !(i.id === item.id && i.type === item.type)));
      setConfirmPurge(null);
    } catch (e) {
      toast.error("Purge failed", { description: formatApiError(e) });
    } finally {
      setPurging(null);
    }
  };

  const autoPurge = async () => {
    setAutoPurgeBusy(true);
    try {
      const { data } = await api.post("/recycle-bin/auto-purge");
      toast.success(`Purged ${data.purged} item(s) older than 30 days`);
      load();
    } catch (e) {
      toast.error("Auto-purge failed", { description: formatApiError(e) });
    } finally {
      setAutoPurgeBusy(false);
    }
  };

  // Admin-only "Empty Recycle Bin" — nukes every soft-deleted row in
  // realtors/brokerages/open_houses regardless of age. Two-tap confirm
  // via `emptyArmed` state (first tap arms, second tap fires) so we
  // never accidentally purge a full bin.
  const [emptyArmed, setEmptyArmed] = useState(false);
  const [emptyBusy, setEmptyBusy] = useState(false);
  const emptyAll = async () => {
    if (!emptyArmed) {
      setEmptyArmed(true);
      setTimeout(() => setEmptyArmed(false), 5000);
      return;
    }
    setEmptyArmed(false);
    setEmptyBusy(true);
    try {
      const { data } = await api.post("/recycle-bin/empty");
      const parts = Object.entries(data.counts || {})
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${v} ${k}`)
        .join(", ");
      toast.success(`Emptied recycle bin — ${data.purged} item(s) gone`, {
        description: parts || "Nothing to purge",
      });
      load();
    } catch (e) {
      toast.error("Empty recycle bin failed", { description: formatApiError(e) });
    } finally {
      setEmptyBusy(false);
    }
  };

  const daysAgo = (iso) => {
    if (!iso) return "?";
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (d === 0) return "today";
    if (d === 1) return "1 day ago";
    return `${d} days ago`;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-4" data-testid="recycle-bin-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-900 flex items-center gap-2">
            <Trash2 className="h-6 w-6 text-slate-400" />
            {isAdmin ? "Master Recycle Bin" : "Recycle Bin"}
            {isAdmin ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                <ShieldCheck className="h-3 w-3" /> All users
              </span>
            ) : null}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin
              ? "Every deletion from every user lands here — your backup if data is removed in error. Items auto-purge after 30 days."
              : "Items you delete are kept here for 30 days. You can restore them; only admins can permanently delete."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={load} disabled={loading} className="min-h-[44px]">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {isAdmin ? (
            <Button
              variant="outline"
              onClick={autoPurge}
              disabled={autoPurgeBusy}
              className="min-h-[44px] text-red-600 border-red-200 hover:bg-red-50"
            >
              {autoPurgeBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
              Purge 30+ days
            </Button>
          ) : null}
          {isAdmin && items.length > 0 ? (
            <Button
              variant={emptyArmed ? "default" : "outline"}
              onClick={emptyAll}
              disabled={emptyBusy}
              className={
                emptyArmed
                  ? "min-h-[44px] bg-red-600 hover:bg-red-700 text-white border-red-700"
                  : "min-h-[44px] text-red-700 border-red-300 hover:bg-red-50"
              }
              data-testid="recycle-bin-empty-all-button"
              title="Permanently delete every item in the recycle bin (admin only, irreversible)"
            >
              {emptyBusy ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {emptyArmed ? `Tap again — empties ${items.length}` : "Empty recycle bin"}
            </Button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12 text-sm text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center">
          <Trash2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Recycle bin is empty.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-slate-500 px-1">{items.length} item(s) in recycle bin</div>
          {items.map((item) => {
            const meta = TYPE_META[item.type] || TYPE_META.realtor;
            const Icon = meta.icon;
            return (
              <div
                key={`${item.type}-${item.id}`}
                className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3"
                data-testid={`recycle-item-${item.type}-${item.id}`}
              >
                <div className={`h-9 w-9 rounded-lg ${meta.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`h-4 w-4 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-slate-900 truncate">{item.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {meta.label}
                    {item.details ? ` · ${item.details}` : ""}
                    {item.realtor_count != null ? ` · ${item.realtor_count} realtors` : ""}
                    {isAdmin && item.deleted_by_email ? (
                      <span className="text-amber-700"> · deleted by {item.deleted_by_email}</span>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400 shrink-0">
                  <Clock className="h-3 w-3" /> {daysAgo(item.deleted_at)}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => restore(item)}
                  disabled={restoring === item.id}
                  className="shrink-0 text-emerald-700 border-emerald-200 hover:bg-emerald-50 min-h-[36px]"
                  data-testid={`recycle-restore-${item.id}`}
                >
                  {restoring === item.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                  )}
                  Restore
                </Button>
                {isAdmin ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmPurge(item)}
                    className="shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 min-h-[36px]"
                    data-testid={`recycle-purge-${item.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm permanent delete */}
      <AlertDialog open={!!confirmPurge} onOpenChange={(v) => !v && setConfirmPurge(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Permanently delete
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{confirmPurge?.name}</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!purging} className="min-h-[44px]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!!purging}
              onClick={(e) => { e.preventDefault(); purge(confirmPurge); }}
              className="bg-red-600 hover:bg-red-700 text-white min-h-[44px]"
            >
              {purging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
